import {
	Bar,
	BarChart,
	CartesianGrid,
	Cell,
	ResponsiveContainer,
	Tooltip,
	XAxis,
	YAxis,
} from "recharts";
import type { DailyPLBar } from "./chart-data";
import { CHART_COLORS, CHART_FONT, CSS_VARS, useChartTheme } from "./chart-theme";

interface Props {
	data: DailyPLBar[];
}

const CustomTooltip = ({
	active,
	payload,
}: {
	active?: boolean;
	payload?: Array<{ payload: DailyPLBar }>;
}) => {
	if (!active || !payload?.[0]) return null;
	const p = payload[0].payload;
	return (
		<div
			className="rounded-md border px-2.5 py-1.5 text-[0.72rem] shadow-lg"
			style={{
				background: CSS_VARS.tooltip,
				borderColor: CSS_VARS.tooltipBorder,
			}}
		>
			<p className="font-semibold tg-text-primary">{p.date}</p>
			<p
				style={{
					color: p.netPL >= 0 ? CHART_COLORS.green : CHART_COLORS.red,
				}}
				className="font-semibold"
			>
				Net P/L: {p.netPL >= 0 ? "+" : ""}${p.netPL.toFixed(2)}
			</p>
			<p className="tg-text-muted">
				{p.trades} trades ({p.wins}W / {p.losses}L)
			</p>
		</div>
	);
};

export const DailyPLChart = ({ data }: Props) => {
	const theme = useChartTheme();

	if (data.length === 0) {
		return (
			<p className="flex h-full items-center justify-center text-[0.78rem] tg-text-muted">
				No daily P/L data available.
			</p>
		);
	}

	return (
		<ResponsiveContainer width="100%" height="100%">
			<BarChart
				data={data}
				margin={{ top: 8, right: 12, bottom: 4, left: 0 }}
			>
				<CartesianGrid
					strokeDasharray="3 3"
					stroke={theme.grid}
					vertical={false}
				/>
				<XAxis
					dataKey="date"
					tick={{ fill: theme.axis, fontSize: CHART_FONT.size }}
					tickLine={false}
					axisLine={{ stroke: theme.grid }}
					interval={data.length > 14 ? Math.floor(data.length / 7) : 0}
				/>
				<YAxis
					tick={{ fill: theme.axis, fontSize: CHART_FONT.size }}
					tickLine={false}
					axisLine={false}
					width={50}
					tickFormatter={(v: number) => `$${v}`}
				/>
				<Tooltip
					content={<CustomTooltip />}
					cursor={{ fill: theme.grid, fillOpacity: 0.5 }}
				/>
				<Bar dataKey="netPL" radius={[4, 4, 0, 0]} maxBarSize={36} isAnimationActive={false}>
					{data.map((entry) => (
						<Cell
							key={entry.dateKey}
							fill={entry.netPL >= 0 ? CHART_COLORS.green : CHART_COLORS.red}
						/>
					))}
				</Bar>
			</BarChart>
		</ResponsiveContainer>
	);
};
